/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationRemoteSessionClientsRotate } from "../funcs/organizationRemoteSessionClientsRotate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RemoteSessionClient } from "../models/components/remotesessionclient.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RotateOrganizationRemoteSessionClientRequest,
  RotateOrganizationRemoteSessionClientSecurity,
} from "../models/operations/rotateorganizationremotesessionclient.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RotateOrganizationRemoteSessionClientMutationVariables = {
  request: RotateOrganizationRemoteSessionClientRequest;
  security?: RotateOrganizationRemoteSessionClientSecurity | undefined;
  options?: RequestOptions;
};

export type RotateOrganizationRemoteSessionClientMutationData =
  RemoteSessionClient;

export type RotateOrganizationRemoteSessionClientMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * rotateClient organizationRemoteSessionClients
 *
 * @remarks
 * Re-register a dynamically registered remote_session_client with its issuer in place, replacing the client_id and secret while keeping the row's id, issuer bindings, MCP server attachments, and key set links. Every remote session minted against the old client_id is revoked, so users reconnect once. Use when the issuer reports the registration expired (upstream_rejected_at is set) or to rotate proactively. The replacement is registered at the registration_endpoint the client's issuer publishes, so the issuer must publish one. Requires org:admin.
 */
export function useRotateOrganizationRemoteSessionClientMutation(
  options?: MutationHookOptions<
    RotateOrganizationRemoteSessionClientMutationData,
    RotateOrganizationRemoteSessionClientMutationError,
    RotateOrganizationRemoteSessionClientMutationVariables
  >,
): UseMutationResult<
  RotateOrganizationRemoteSessionClientMutationData,
  RotateOrganizationRemoteSessionClientMutationError,
  RotateOrganizationRemoteSessionClientMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRotateOrganizationRemoteSessionClientMutation(client, options),
    ...options,
  });
}

export function mutationKeyRotateOrganizationRemoteSessionClient(): MutationKey {
  return ["@gram/client", "organizationRemoteSessionClients", "rotate"];
}

export function buildRotateOrganizationRemoteSessionClientMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RotateOrganizationRemoteSessionClientMutationVariables,
  ) => Promise<RotateOrganizationRemoteSessionClientMutationData>;
} {
  return {
    mutationKey: mutationKeyRotateOrganizationRemoteSessionClient(),
    mutationFn: function rotateOrganizationRemoteSessionClientMutationFn({
      request,
      security,
      options,
    }): Promise<RotateOrganizationRemoteSessionClientMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(organizationRemoteSessionClientsRotate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
